const isPresent = (value) => value !== null && value !== undefined && value !== '';

const finiteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Resolve customer amounts without exposing internal cost for explicit retail zero. */
export const resolveOtherServiceCustomerAmounts = (service = {}) => {
  const explicitSubtotalValue = service?.Subtotal ?? service?.subtotal ?? service?.subTotal;
  const explicitSubtotal = finiteNumber(explicitSubtotalValue, 0);
  const retailValue = service?.RetailPrice ?? service?.retailPrice;
  const hasExplicitRetail = isPresent(retailValue);
  const quantity = finiteNumber(service?.Quantity ?? service?.quantity, 0);
  // OtherServices.Taxes is saved in percent units (1 means 1%, not 100%).
  // Preserve legacy Tax/taxRate interpretation only when Taxes is absent.
  const storedPercent = isPresent(service?.Taxes);
  const rateRaw = finiteNumber(storedPercent ? service.Taxes : (service?.Tax ?? service?.taxRate), 0);
  const taxRate = storedPercent ? rateRaw / 100 : (rateRaw > 1 ? rateRaw / 100 : rateRaw);
  const explicitTaxValue = service?.TaxAmount ?? service?.taxAmount ?? service?.tax;
  const explicitTax = finiteNumber(explicitTaxValue, 0);

  if (isPresent(explicitSubtotalValue) && explicitSubtotal > 0) {
    const tax = isPresent(explicitTaxValue) && explicitTax > 0
      ? explicitTax
      : explicitSubtotal * taxRate;
    return { quantity, subtotal: explicitSubtotal, tax, total: explicitSubtotal + tax, taxRate };
  }

  if (hasExplicitRetail) {
    const subtotal = finiteNumber(retailValue, 0) * quantity;
    const tax = isPresent(explicitTaxValue) && explicitTax > 0
      ? explicitTax
      : subtotal * taxRate;
    return { quantity, subtotal, tax, total: subtotal + tax, taxRate };
  }

  const totalValue = service?.Total ?? service?.total;
  if (isPresent(totalValue) && Number.isFinite(Number(totalValue))) {
    const total = Number(totalValue);
    const subtotal = isPresent(explicitTaxValue) && explicitTax > 0
      ? total - explicitTax
      : taxRate > 0 ? total / (1 + taxRate) : total;
    return { quantity, subtotal, tax: Math.max(0, total - subtotal), total, taxRate };
  }

  // Preserve the legacy fallback only when retail is genuinely absent.
  const subtotal = finiteNumber(service?.UnitCost ?? service?.unitCost, 0) * quantity;
  const tax = subtotal * taxRate;
  return { quantity, subtotal, tax, total: subtotal + tax, taxRate };
};

export const sumOtherServiceCustomerAmounts = (services = [], { roundServiceTaxPerLine = false } = {}) => {
  const rows = Array.isArray(services) ? services : services ? [services] : [];
  // Preserve the historical Order Cart and invoice-owned aggregation path.
  // The live Sales Order Cart explicitly opts into integer-cent service tax
  // summation to match its generated document and server invoiceable totals.
  return rows.reduce((totals, service) => {
    if (service?.isDeleted === true || service?.isDeleted === 'true') return totals;
    const amounts = resolveOtherServiceCustomerAmounts(service);
    if (!roundServiceTaxPerLine) {
      totals.subtotal += amounts.subtotal;
      totals.tax += amounts.tax;
      totals.total += amounts.total;
      return totals;
    }
    const subtotalCents = Math.round(totals.subtotal * 100) + Math.round(amounts.subtotal * 100);
    const taxCents = Math.round(totals.tax * 100) + Math.round(amounts.tax * 100);
    totals.subtotal = subtotalCents / 100;
    totals.tax = taxCents / 100;
    totals.total = (subtotalCents + taxCents) / 100;
    return totals;
  }, { subtotal: 0, tax: 0, total: 0 });
};

/** Resolve an Other Service's chosen tax by catalogue identity, not by rate.
 * Different configured tax records may legitimately share the same percentage.
 */
export const findOtherServiceTaxById = (taxRows = [], taxId = null) => {
  if (taxId === null || taxId === undefined || taxId === '') return null;
  return (Array.isArray(taxRows) ? taxRows : []).find((row) =>
    String(row?.id ?? row?.taxId ?? row?.ID ?? '') === String(taxId)
  ) || null;
};

/**
 * Align saved Sales Order Cart shipping tax fields after a deliberate edit.
 * Keep the original nested source intact; it represents the original Order
 * Cart and must not be rewritten by a snapshot-only edit.
 */
export const alignEditedShippingServiceTax = (originalService = {}, editedService = {}, selectedTax = null) => {
  const merged = { ...originalService, ...editedService };
  if (String(merged?.Type ?? merged?.type ?? '').trim().toLowerCase() !== 'shipping') return merged;

  // OtherServices.Taxes stores percent values (e.g. 13, not 0.13).
  const percent = Number(editedService?.Taxes);
  const rate = Number.isFinite(percent) && percent > 0 ? percent / 100 : 0;
  const quantity = Number(editedService?.Quantity ?? merged?.Quantity) || 1;
  const retail = Number(editedService?.RetailPrice ?? merged?.RetailPrice);
  if (!Number.isFinite(retail)) return merged;

  // Round the customer-facing service tax once at the individual line,
  // consistently with the server's Sales Order snapshot calculation.
  const subtotalCents = Math.round(retail * quantity * 100);
  const taxCents = Math.round(subtotalCents * rate);
  const subtotal = subtotalCents / 100;
  const taxAmount = taxCents / 100;
  const total = (subtotalCents + taxCents) / 100;
  const numericTaxId = Number(editedService?.TaxId);
  const taxId = editedService?.TaxId == null || !Number.isFinite(numericTaxId) || numericTaxId <= 0
    ? null : numericTaxId;
  const taxIds = taxId == null ? [] : [taxId];
  const taxDetails = taxId == null ? null : {
    taxId,
    rate,
    ...(selectedTax?.type ? { type: selectedTax.type } : {}),
    ...(selectedTax?.stateCode ? { stateCode: selectedTax.stateCode } : {}),
    ...(selectedTax?.countryCode ? { countryCode: selectedTax.countryCode } : {}),
  };

  return {
    ...merged,
    Taxes: editedService?.Taxes ?? null,
    taxes: editedService?.Taxes ?? null,
    TaxId: taxId,
    taxId,
    taxIds,
    TaxIds: taxIds,
    taxRate: rate,
    Subtotal: subtotal,
    subtotal,
    TaxAmount: taxAmount,
    taxAmount,
    tax: taxAmount,
    // The legacy uppercase Tax sometimes holds a dollar amount; invoice tax
    // ID collection must not interpret that amount as a catalog tax ID.
    Tax: null,
    Total: total,
    total,
    taxDetails,
    taxDetailsList: taxDetails ? [taxDetails] : [],
  };
};

export const isDecimalInput = (value) => /^\d*(?:[.,]\d*)?$/.test(String(value));
export const isWholeNumberInput = (value) => /^\d*$/.test(String(value));
export const decimalInputToNumber = (value) => finiteNumber(String(value ?? '').replace(',', '.'), 0);
